import React, { useState, useEffect } from 'react';
import { FileText, Loader2, AlertTriangle, ChevronDown } from 'lucide-react';
import { apiService } from '../services/api';
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import { RecentIndication } from '../types';

interface CancerType {
  id: string;
  name: string;
}

interface Indication {
  id: string;
  name: string;
}

interface ReportResult {
  url?: string;
  indication_name?: string;
  message?: string;
  error?: string;
  rate_limited?: boolean;
  reset_time?: string;
  current_count?: number;
  limit?: number;
}

const ReportOrder: React.FC = () => {
  const navigate = useNavigate();
  const [cancerTypes, setCancerTypes] = useState<CancerType[]>([]);
  const [indications, setIndications] = useState<Indication[]>([]);
  const [selectedCancerType, setSelectedCancerType] = useState<string>('');
  const [selectedIndication, setSelectedIndication] = useState<string>('');
  const [selectedNccnType, setSelectedNccnType] = useState<string>('nccn');
  const [loading, setLoading] = useState(false);
  const [loadingIndications, setLoadingIndications] = useState(false);
  const [result, setResult] = useState<ReportResult | null>(null);
  const [recentIndications, setRecentIndications] = useState<RecentIndication[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(false);

  // Fetch cancer types on mount
  useEffect(() => {
    const fetchCancerTypes = async () => {
      try {
        const data = await apiService.fetchCancerTypes();
        setCancerTypes(data);
      } catch (error) {
        console.error('Error fetching cancer types:', error);
      }
    };
    fetchCancerTypes();
  }, []);

  // Fetch indications when cancer type changes
  useEffect(() => {
    const fetchIndications = async () => {
      if (!selectedCancerType) {
        setIndications([]);
        setSelectedIndication('');
        return;
      }

      try {
        setLoadingIndications(true);
        const data = await apiService.fetchIndications(selectedCancerType);
        setIndications(data);
      } catch (error) {
        console.error('Error fetching indications:', error);
        setIndications([]);
      } finally {
        setLoadingIndications(false);
      }
    };

    fetchIndications();
  }, [selectedCancerType]);

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        setLoadingRecent(true);
        const data = await apiService.fetchRecentIndications();
        setRecentIndications(data);
      } catch (error) {
        console.error('Error fetching recent indications:', error);
      } finally {
        setLoadingRecent(false);
      }
    };
    fetchRecent();
  }, []);

  const formatRecentDate = (dateStr: string | null): string => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (e) {
      return dateStr;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedIndication || !selectedNccnType) {
      setResult({
        error: 'Please select both Indication and NCCN Type.'
      });
      return;
    }

    try {
      setLoading(true);
      setResult(null);

      const data = await apiService.generateReport({
        indication_code: selectedIndication,
        nccn_type: selectedNccnType
      });
      if (data?.url) {
        navigate(data.url);
        return;
      }
      setResult(data);
    } catch (error: any) {
      console.error('Error generating report:', error);
      setResult({
        error: error.response?.data?.error || error.message || 'Failed to generate report. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-slate-900 pb-20 relative bg-slate-50 font-sans">
      <Header />
      <div className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6 py-10 space-y-8">
        <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-8 text-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Order SOC Report</h1>
            <p className="text-sm text-slate-500 mt-1">Choose a cancer type and indication.</p>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 max-w-xl mx-auto space-y-4 text-left">
            <label className="space-y-2 text-sm text-slate-600 block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Cancer type</span>
              <div className="relative">
                <select
                  value={selectedCancerType}
                  onChange={(e) => {
                    setSelectedCancerType(e.target.value);
                    setSelectedIndication('');
                  }}
                  className="appearance-none w-full bg-white border border-slate-200 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 cursor-pointer"
                  required
                  disabled={loading}
                >
                  <option value="">Select cancer type</option>
                  {cancerTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </label>

            <label className="space-y-2 text-sm text-slate-600 block">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Indication</span>
              <div className="relative">
                <select
                  value={selectedIndication}
                  onChange={(e) => setSelectedIndication(e.target.value)}
                  className="appearance-none w-full bg-white border border-slate-200 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
                  required
                  disabled={!selectedCancerType || loadingIndications || loading}
                >
                  <option value="">
                    {loadingIndications
                      ? 'Loading...'
                      : !selectedCancerType
                        ? 'Select cancer type first'
                        : 'Select indication'}
                  </option>
                  {indications.map((ind) => (
                    <option key={ind.id} value={ind.id}>
                      {ind.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </label>

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating report...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    Order report
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="max-w-3xl mx-auto">
          <details className="bg-white rounded-[2rem] shadow-card border border-gray-100 px-6 py-4">
            <summary className="cursor-pointer text-sm font-semibold text-slate-700">
              Previously ordered reports
            </summary>
            <div className="mt-4">
              {loadingRecent && (
                <div className="text-sm text-slate-500">Loading recent reports…</div>
              )}
              {!loadingRecent && recentIndications.length === 0 && (
                <div className="text-sm text-slate-500">No recent reports yet.</div>
              )}
              {!loadingRecent && recentIndications.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {recentIndications.map((item) => (
                    <button
                      key={`${item.indication_code}-${item.nccn_type}`}
                      type="button"
                      onClick={() => navigate(`/reports/${item.indication_code}/${item.nccn_type}`)}
                      className="text-left rounded-xl border border-slate-200 px-4 py-3 hover:bg-indigo-50 transition-colors"
                    >
                      <div className="text-sm font-semibold text-slate-900">{item.indication_name}</div>
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                        <span className="uppercase">{item.nccn_type || 'nccn'}</span>
                        <span>v{item.nccn_version}</span>
                        <span>•</span>
                        <span>{formatRecentDate(item.last_accessed)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </details>
        </div>

        {result?.error && (
          <div className="max-w-xl mx-auto">
            <div className="rounded-2xl border border-red-100 bg-red-50 px-6 py-4 text-sm text-red-700">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 mt-0.5" />
                <div>
                  <p className="font-semibold">Error</p>
                  <p className="mt-1">{result.error}</p>
                  {result.rate_limited && (
                    <p className="text-xs mt-1">
                      Current: {result.current_count}/{result.limit}.
                      {result.reset_time && ` Reset: ${new Date(result.reset_time).toLocaleString()}`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportOrder;
